import React, { useState, useEffect } from 'react';
import { CreditCard, TrendingUp, DollarSign, Users, Calendar, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { clientAPI, financialAPI } from '../../services/api';
import { getAppointments } from '../../services/appointmentApi';
import { getEmployeeRankings } from '../../services/ratingApi';
import { useToast } from '../Toast';
import { WidgetGrid } from '../WidgetGrid';
import { WidgetSettingsModal } from '../WidgetSettingsModal';
import { DashboardCustomizeHeader } from '../DashboardCustomizeHeader';
import { getCatalogForRole, type DashboardRoleKey } from '../../config/dashboardWidgetCatalog';
import { useWidgetPreferences } from '../../hooks/useWidgetPreferences';
import LimitsWidget from './LimitsWidget';
import { DEMO_ADMIN_STATS } from '../../data/demoDashboardData';

interface DashboardStats {
  totalClients: number;
  totalRevenue: number;
  monthlyRevenue: number;
  totalAppointments: number;
  completedToday: number;
  completedThisMonth: number;
  pendingPayments: number;
  overduePayments: number;
  completionRate: number;
  cancellationRate: number;
  averageTicket: number;
  employeeRankings: Array<{ id: string; name: string; rating: number; completed: number }>;
  monthlyTrend: Array<{ month: string; revenue: number; appointments: number; completed: number }>;
}

interface AdminDashboardProps {
  isDemoMode?: boolean;
  dashboardRoleKey?: DashboardRoleKey;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  isDemoMode = false,
  dashboardRoleKey = 'admin',
}) => {
  const { t, formatCurrency } = useI18n();
  const { showError } = useToast();
  const {
    preferences,
    updateWidgetVisibility,
    updateWidgetOrder,
    getVisibleWidgets,
    flushSave,
    isLoading: prefsLoading,
    setLayout,
    resetToDefault,
  } = useWidgetPreferences(dashboardRoleKey);
  const [isCustomizeMode, setIsCustomizeMode] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalClients: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalAppointments: 0,
    completedToday: 0,
    completedThisMonth: 0,
    pendingPayments: 0,
    overduePayments: 0,
    completionRate: 0,
    cancellationRate: 0,
    averageTicket: 0,
    employeeRankings: [],
    monthlyTrend: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);

  useEffect(() => {
    if (isDemoMode) {
      setStats({ ...DEMO_ADMIN_STATS, monthlyTrend: [...DEMO_ADMIN_STATS.monthlyTrend], employeeRankings: [...DEMO_ADMIN_STATS.employeeRankings] });
      setIsLoading(false);
      return;
    }
    loadDashboardData();
  }, [isDemoMode]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Carregar clientes
      let clients: any[] = [];
      try {
        const clientsRes = await clientAPI.get('/clients');
        clients = clientsRes.data.clients || clientsRes.data || [];
      } catch (err) {
        console.warn('Erro ao carregar clientes (não crítico):', err);
        clients = [];
      }
      
      // Carregar agendamentos
      let appointmentsArray: any[] = [];
      try {
        const appointments = await getAppointments();
        if (appointments) {
          appointmentsArray = Array.isArray(appointments) 
            ? appointments 
            : (appointments?.appointments || appointments?.data || []);
        } else {
          // getAppointments retorna null quando não há dados ou há erro não crítico
          // Isso é esperado e não deve gerar erro
          appointmentsArray = [];
        }
      } catch (err: any) {
        // getAppointments não deve lançar exceções, mas se lançar, tratar silenciosamente
        // Log mais detalhado para debug apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          if (err.response) {
            console.warn('Erro ao carregar agendamentos (não crítico):', {
              status: err.response.status,
              statusText: err.response.statusText,
              data: err.response.data,
            });
          } else if (err.request) {
            console.warn('Erro de rede ao carregar agendamentos - servidor não respondeu (não crítico)');
          } else {
            console.warn('Erro ao carregar agendamentos (não crítico):', err.message);
          }
        }
        // Continuar sem agendamentos - não é crítico para o dashboard
        appointmentsArray = [];
      }
      
      // Carregar dados financeiros
      let financialData = { total: 0, monthly: 0, pending: 0, overdue: 0 };
      let payments: any[] = []; // Declarar payments no escopo correto
      try {
        const paymentsRes = await financialAPI.get('/financial/payments');
        payments = paymentsRes.data.payments || paymentsRes.data || [];
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        
        financialData = {
          total: payments.reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0),
          monthly: payments
            .filter((p: any) => {
              const paymentDate = new Date(p.payment_date || p.created_at);
              return paymentDate >= firstDayOfMonth;
            })
            .reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0),
          pending: payments.filter((p: any) => p.status === 'pending' || p.status === 'pendente').length,
          overdue: payments.filter((p: any) => {
            if (p.status === 'paid' || p.status === 'pago') return false;
            const dueDate = new Date(p.due_date || p.payment_date);
            return dueDate < today;
          }).length,
        };
      } catch (err) {
        // Erro ao carregar dados financeiros - continuar sem eles (não crítico)
        console.warn('Erro ao carregar dados financeiros (não crítico):', err);
      }

      // Calcular estatísticas
      const today = new Date();
      const todayISO = today.toISOString().split('T')[0];
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      
      const completedToday = appointmentsArray.filter((apt: any) => {
        const aptDate = new Date(apt.scheduled_date || apt.completed_at);
        return apt.status === 'completed' || apt.status === 'concluído' || apt.status === 'concluido';
      }).filter((apt: any) => {
        const aptDate = new Date(apt.scheduled_date || apt.completed_at);
        return aptDate.toISOString().split('T')[0] === todayISO;
      }).length;

      const completedThisMonth = appointmentsArray.filter((apt: any) => {
        const aptDate = new Date(apt.scheduled_date || apt.completed_at);
        return (apt.status === 'completed' || apt.status === 'concluído' || apt.status === 'concluido') &&
               aptDate >= firstDayOfMonth;
      }).length;

      const completionRate = appointmentsArray.length > 0
        ? Math.round((completedThisMonth / appointmentsArray.length) * 100)
        : 0;

      // Calcular taxa de cancelamento
      const cancelledCount = appointmentsArray.filter(
        (apt: any) => apt.status === 'cancelled' || apt.status === 'cancelado'
      ).length;
      const cancellationRate = appointmentsArray.length > 0
        ? Math.round((cancelledCount / appointmentsArray.length) * 100 * 10) / 10
        : 0;

      // Calcular ticket médio
      const completedWithPrice = appointmentsArray.filter(
        (apt: any) => (apt.status === 'completed' || apt.status === 'concluído' || apt.status === 'concluido') && apt.price
      );
      const averageTicket = completedWithPrice.length > 0
        ? completedWithPrice.reduce((sum: number, apt: any) => sum + (parseFloat(apt.price) || 0), 0) / completedWithPrice.length
        : 0;

      // Calcular ranking de funcionários (por número de serviços completados)
      const employeeStats: Record<string, { name: string; completed: number }> = {};
      appointmentsArray.forEach((apt: any) => {
        if (apt.status === 'completed' || apt.status === 'concluído' || apt.status === 'concluido') {
          const empId = apt.employee_id || 'unknown';
          if (!employeeStats[empId]) {
            employeeStats[empId] = { name: apt.employee_name || 'Desconhecido', completed: 0 };
          }
          employeeStats[empId].completed++;
        }
      });

      // Buscar rankings com ratings reais da API
      let employeeRankingsWithRatings: Array<{ id: string; name: string; rating: number; completed: number }> = [];
      try {
        const rankingsResponse = await getEmployeeRankings({ limit: 10, min_ratings: 1 });
        // Garantir que rankings seja sempre um array
        let rankings: any[] = [];
        if (rankingsResponse) {
          if (Array.isArray(rankingsResponse.rankings)) {
            rankings = rankingsResponse.rankings;
          } else if (Array.isArray(rankingsResponse)) {
            rankings = rankingsResponse;
          } else if (rankingsResponse.rankings && typeof rankingsResponse.rankings === "object") {
            rankings = Object.values(rankingsResponse.rankings);
          } else if (typeof rankingsResponse === "object") {
            rankings = Object.values(rankingsResponse);
          }
        }
        
        // Criar mapa de ratings por employee_id
        const ratingMap = new Map<string, number>();
        rankings.forEach((r: any) => {
          if (r && r.employee_id) {
            ratingMap.set(r.employee_id, r.average_rating || 0);
          }
        });

        // Combinar dados de completados com ratings
        employeeRankingsWithRatings = Object.entries(employeeStats)
          .map(([id, data]) => ({
            id,
            name: data.name,
            rating: ratingMap.get(id) || 0,
            completed: data.completed,
          }))
          .sort((a, b) => {
            // Ordenar primeiro por rating (se > 0), depois por completed
            if (a.rating > 0 && b.rating > 0) {
              return b.rating - a.rating;
            }
            if (a.rating > 0) return -1;
            if (b.rating > 0) return 1;
            return b.completed - a.completed;
          })
          .slice(0, 5); // Top 5
      } catch (err) {
        // Fallback para ranking sem ratings se API falhar
        console.warn('Erro ao carregar rankings de avaliação:', err);
        employeeRankingsWithRatings = Object.entries(employeeStats)
          .map(([id, data]) => ({
            id,
            name: data.name,
            rating: 0,
            completed: data.completed,
          }))
          .sort((a, b) => b.completed - a.completed)
          .slice(0, 5);
      }

      const employeeRankings = employeeRankingsWithRatings;

      // Calcular tendência mensal (últimos 6 meses)
      const monthlyTrend: Array<{ month: string; revenue: number; appointments: number; completed: number }> = [];
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const nextMonthDate = new Date(today.getFullYear(), today.getMonth() - i + 1, 1);
        const monthKey = `${monthNames[monthDate.getMonth()]}/${monthDate.getFullYear().toString().slice(-2)}`;
        
        const monthPayments = payments.filter((p: any) => {
          const paymentDate = new Date(p.payment_date || p.created_at);
          return paymentDate >= monthDate && paymentDate < nextMonthDate;
        });
        
        const monthAppointments = appointmentsArray.filter((apt: any) => {
          const aptDate = new Date(apt.scheduled_date || apt.created_at);
          return aptDate >= monthDate && aptDate < nextMonthDate;
        });
        
        const monthCompleted = monthAppointments.filter((apt: any) => 
          apt.status === 'completed' || apt.status === 'concluído' || apt.status === 'concluido'
        ).length;
        
        monthlyTrend.push({
          month: monthKey,
          revenue: monthPayments.reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0),
          appointments: monthAppointments.length,
          completed: monthCompleted,
        });
      }

      setStats({
        totalClients: clients.length,
        totalRevenue: financialData.total,
        monthlyRevenue: financialData.monthly,
        totalAppointments: appointmentsArray.length,
        completedToday,
        completedThisMonth,
        pendingPayments: financialData.pending,
        overduePayments: financialData.overdue,
        completionRate,
        cancellationRate,
        averageTicket,
        employeeRankings,
        monthlyTrend,
      });
    } catch (error: any) {
      // Não mostrar erro genérico se for apenas falta de dados de agendamentos
      // O erro já foi tratado individualmente acima
      if (error?.message && error.message.includes('agendamentos')) {
        console.warn('Erro ao carregar agendamentos (não crítico):', error);
      } else {
        // Só mostrar erro se for algo realmente crítico
        console.error('Erro ao carregar dashboard:', error);
        // Não mostrar toast de erro genérico - os erros específicos já foram tratados
      }
    } finally {
      setIsLoading(false);
    }
  };

  const StatCard: React.FC<{ icon: React.ElementType; label: string; value: string | number; color: string }> = ({
    icon: Icon,
    label,
    value,
    color,
  }) => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
      <Icon style={{ fontSize: '24px', color, marginRight: '10px' }} />
      <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>{label}</h3>
    </div>
  );

  // Componente de gráfico simples de linha
  const SimpleLineChart: React.FC<{ data: Array<{ month: string; revenue: number; appointments: number; completed: number }> }> = ({ data }) => {
    const width = 400;
    const height = 200;
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Normalizar valores para o gráfico
    const maxRevenue = Math.max(...data.map(d => d.revenue), 1);
    const maxAppointments = Math.max(...data.map(d => d.appointments), 1);
    const maxValue = Math.max(maxRevenue, maxAppointments);

    // Gerar pontos para receita
    const revenuePoints = data.map((d, i) => {
      const x = padding + (i / (data.length - 1 || 1)) * chartWidth;
      const y = height - padding - (d.revenue / maxValue) * chartHeight;
      return `${x},${y}`;
    }).join(' ');

    // Gerar pontos para agendamentos
    const appointmentsPoints = data.map((d, i) => {
      const x = padding + (i / (data.length - 1 || 1)) * chartWidth;
      const y = height - padding - (d.appointments / maxValue) * chartHeight;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <svg width={width} height={height} style={{ display: 'block' }}>
          {/* Eixos */}
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#e5e7eb" strokeWidth="2" />
          <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#e5e7eb" strokeWidth="2" />
          
          {/* Linha de receita */}
          <polyline
            points={revenuePoints}
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Linha de agendamentos */}
          <polyline
            points={appointmentsPoints}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Pontos e labels */}
          {data.map((d, i) => {
            const x = padding + (i / (data.length - 1 || 1)) * chartWidth;
            const revenueY = height - padding - (d.revenue / maxValue) * chartHeight;
            const appointmentsY = height - padding - (d.appointments / maxValue) * chartHeight;
            
            return (
              <g key={i}>
                <circle cx={x} cy={revenueY} r="4" fill="#10b981" />
                <circle cx={x} cy={appointmentsY} r="4" fill="#3b82f6" />
                <text
                  x={x}
                  y={height - padding + 20}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#666"
                >
                  {d.month}
                </text>
              </g>
            );
          })}
        </svg>
        <div style={{ display: 'flex', gap: '20px', marginTop: '10px', fontSize: '12px', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#10b981', borderRadius: '2px' }}></div>
            <span>Receita</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#3b82f6', borderRadius: '2px' }}></div>
            <span>{t('appointments')}</span>
          </div>
        </div>
      </div>
    );
  };

  const availableWidgets = [
    { id: 'totalRevenue', title: 'Receita Total', description: 'Receita total acumulada' },
    { id: 'monthlyRevenue', title: 'Receita Mensal', description: 'Receita do mês atual' },
    { id: 'pendingPayments', title: 'Pagamentos Pendentes', description: 'Pagamentos aguardando processamento' },
    { id: 'overduePayments', title: 'Pagamentos Atrasados', description: 'Pagamentos com vencimento passado' },
    { id: 'totalClients', title: 'Total de Clientes', description: 'Número total de clientes cadastrados' },
    { id: 'totalAppointments', title: 'Total de Agendamentos', description: 'Total de agendamentos no sistema' },
    { id: 'completedToday', title: 'Completados Hoje', description: 'Serviços completados hoje' },
    { id: 'completionRate', title: 'Taxa de Conclusão', description: 'Percentual de serviços completados' },
    { id: 'cancellationRate', title: 'Taxa de Cancelamento', description: 'Percentual de serviços cancelados' },
    { id: 'averageTicket', title: 'Ticket Médio', description: 'Valor médio por serviço' },
    { id: 'employeeRankings', title: 'Ranking de Funcionários', description: 'Top 5 funcionários por serviços completados' },
    { id: 'monthlyTrend', title: 'Tendência Mensal', description: 'Gráfico de receita e agendamentos dos últimos 6 meses' },
    { id: 'limits', title: 'Limites de Recursos', description: 'Alertas de limites de usuários, funcionários e clientes' },
  ];

  const catalogIds = new Set(getCatalogForRole(dashboardRoleKey));

  const allWidgets = [
    {
      id: 'totalRevenue',
      title: 'Receita Total',
      content: (
        <div>
          <StatCard icon={DollarSign} label="Receita Total" value={formatCurrency(stats.totalRevenue)} color="#10b981" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981', marginTop: '10px' }}>
            {isLoading ? '...' : formatCurrency(stats.totalRevenue)}
          </div>
        </div>
      ),
    },
    {
      id: 'monthlyRevenue',
      title: 'Receita Mensal',
      content: (
        <div>
          <StatCard icon={TrendingUp} label="Receita Mensal" value="" color="#3b82f6" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6', marginTop: '10px' }}>
            {isLoading ? '...' : `R$ ${stats.monthlyRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>
        </div>
      ),
    },
    {
      id: 'pendingPayments',
      title: 'Pagamentos Pendentes',
      content: (
        <div>
          <StatCard icon={CreditCard} label="Pagamentos Pendentes" value="" color="#f59e0b" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b', marginTop: '10px' }}>
            {isLoading ? '...' : stats.pendingPayments}
          </div>
        </div>
      ),
    },
    {
      id: 'overduePayments',
      title: 'Pagamentos Atrasados',
      content: (
        <div>
          <StatCard icon={AlertCircle} label="Pagamentos Atrasados" value="" color="#dc2626" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc2626', marginTop: '10px' }}>
            {isLoading ? '...' : stats.overduePayments}
          </div>
        </div>
      ),
    },
    {
      id: 'totalClients',
      title: 'Total de Clientes',
      content: (
        <div>
          <StatCard icon={Users} label="Total de Clientes" value="" color="#8b5cf6" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#8b5cf6', marginTop: '10px' }}>
            {isLoading ? '...' : stats.totalClients}
          </div>
        </div>
      ),
    },
    {
      id: 'totalAppointments',
      title: 'Total de Agendamentos',
      content: (
        <div>
          <StatCard icon={Calendar} label="Total de Agendamentos" value="" color="#9333ea" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9333ea', marginTop: '10px' }}>
            {isLoading ? '...' : stats.totalAppointments}
          </div>
        </div>
      ),
    },
    {
      id: 'completedToday',
      title: 'Completados Hoje',
      content: (
        <div>
          <StatCard icon={CheckCircle} label="Completados Hoje" value="" color="#10b981" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981', marginTop: '10px' }}>
            {isLoading ? '...' : stats.completedToday}
          </div>
        </div>
      ),
    },
    {
      id: 'completionRate',
      title: 'Taxa de Conclusão',
      content: (
        <div>
          <StatCard icon={Clock} label={t('dashboard.completionRate')} value="" color="#3b82f6" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6', marginTop: '10px' }}>
            {isLoading ? '...' : `${stats.completionRate}%`}
          </div>
        </div>
      ),
    },
    {
      id: 'cancellationRate',
      title: 'Taxa de Cancelamento',
      content: (
        <div>
          <StatCard icon={AlertCircle} label="Taxa de Cancelamento" value="" color="#f59e0b" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b', marginTop: '10px' }}>
            {isLoading ? '...' : `${stats.cancellationRate}%`}
          </div>
        </div>
      ),
    },
    {
      id: 'averageTicket',
      title: 'Ticket Médio',
      content: (
        <div>
          <StatCard icon={DollarSign} label="Ticket Médio" value="" color="#10b981" />
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981', marginTop: '10px' }}>
            {isLoading ? '...' : formatCurrency(stats.averageTicket)}
          </div>
        </div>
      ),
    },
    {
      id: 'employeeRankings',
      title: 'Ranking de Funcionários',
      content: stats.employeeRankings.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {stats.employeeRankings.map((emp, index) => (
            <div
              key={emp.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                backgroundColor: index === 0 ? '#fef3c7' : '#f9fafb',
                borderRadius: '6px',
                border: index === 0 ? '2px solid #f59e0b' : '1px solid #e5e7eb',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: index === 0 ? '#f59e0b' : '#6b7280',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                  }}
                >
                  {index + 1}
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#374151' }}>{emp.name}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {emp.completed} serviço{emp.completed !== 1 ? 's' : ''} completado{emp.completed !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              {emp.rating > 0 && (
                <div style={{ fontSize: '14px', color: '#f59e0b', fontWeight: '600' }}>
                  ⭐ {emp.rating.toFixed(1)}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: '#666', fontSize: '14px' }}>{t('adminDashboard.noEmployeesCompleted')}</div>
      ),
    },
    {
      id: 'monthlyTrend',
      title: 'Tendência Mensal',
      content: stats.monthlyTrend.length > 0 ? (
        <div>
          <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>{t('adminDashboard.last6Months')}</div>
          <SimpleLineChart data={stats.monthlyTrend} />
        </div>
      ) : (
        <div style={{ color: '#666', fontSize: '14px' }}>{t('adminDashboard.loadingTrend')}</div>
      ),
    },
    {
      id: 'limits',
      title: 'Limites de Recursos',
      content: <LimitsWidget compact={true} />,
    },
  ];

  const widgets = allWidgets.filter(w => catalogIds.has(w.id));
  const availableWidgetsFiltered = availableWidgets.filter(w => catalogIds.has(w.id));

  const dashboardTitle =
    dashboardRoleKey === 'supervisor' ? t('dashboardSupervisorTitle') : t('dashboardAdminTitle');
  const dashboardSubtitle =
    dashboardRoleKey === 'supervisor'
      ? t('dashboardSupervisorSubtitle')
      : t('dashboardAdminSubtitle');

  const handleToggleCustomize = async () => {
    if (isCustomizeMode) {
      try {
        await flushSave();
      } catch {
        showError(t('dashboardSaveError'));
      }
    }
    setIsCustomizeMode(prev => !prev);
  };

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{dashboardTitle}</h1>
          <p style={{ color: '#666', margin: 0 }}>{dashboardSubtitle}</p>
        </div>
        <DashboardCustomizeHeader
          isCustomizeMode={isCustomizeMode}
          onToggleCustomize={handleToggleCustomize}
          onOpenSettings={() => setShowWidgetSettings(true)}
        />
      </div>

      <WidgetGrid
        widgets={widgets}
        preferences={preferences}
        isLoading={prefsLoading}
        editable={isCustomizeMode}
        columns={dashboardRoleKey === 'supervisor' ? 3 : 4}
        onReorder={updateWidgetOrder}
        onToggleVisibility={updateWidgetVisibility}
        getVisibleWidgets={getVisibleWidgets}
      />

      {/* Alertas e Ações Rápidas */}
      {(stats.overduePayments > 0 || stats.pendingPayments > 0) && (
        <div
          style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '30px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <AlertCircle style={{ fontSize: '20px', color: '#f59e0b', marginRight: '10px' }} />
            <h3 style={{ margin: 0, color: '#92400e' }}>{t('adminDashboard.attentionRequired')}</h3>
          </div>
          <div style={{ color: '#92400e' }}>
            {stats.overduePayments > 0 && (
              <p style={{ margin: '5px 0' }}>
                ⚠️ {stats.overduePayments} pagamento(s) atrasado(s) precisam de atenção
              </p>
            )}
            {stats.pendingPayments > 0 && (
              <p style={{ margin: '5px 0' }}>
                📋 {stats.pendingPayments} pagamento(s) pendente(s) aguardando processamento
              </p>
            )}
          </div>
        </div>
      )}

      {/* Widget Settings Modal */}
      <WidgetSettingsModal
        isOpen={showWidgetSettings}
        onClose={() => setShowWidgetSettings(false)}
        preferences={preferences}
        availableWidgets={availableWidgetsFiltered}
        onToggleVisibility={updateWidgetVisibility}
        onSetLayout={setLayout}
        onReset={() => resetToDefault()}
      />
    </div>
  );
};

export default AdminDashboard;

